// System health — single page that surfaces worker status, queue depths, and
// at-a-glance counts so an admin can tell if anything is broken without SSH.

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Activity, Bell, Cpu, ShieldAlert, Sparkles, RefreshCw } from 'lucide-react';
import { api } from '../lib/api';

export default function Health() {
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['system-health'],
    queryFn: () => api.onboarding.health(),
    refetchInterval: 30_000,
  });

  const items = [
    {
      icon: <Bell />, label: 'Unread alerts', key: 'unread_alerts',
      to: '/alerts', threshold: { warn: 5, error: 20 },
    },
    {
      icon: <Cpu />, label: 'At-risk firmware (devices on advisories)', key: 'firmware_at_risk',
      to: '/firmware-history', threshold: { warn: 1, error: 10 },
    },
    {
      icon: <ShieldAlert />, label: 'Certs expiring within 30 days', key: 'certs_expiring_30d',
      to: '/alerts', threshold: { warn: 1, error: 1 },
    },
    {
      icon: <Activity />, label: 'Calls (cl_calls) in last hour', key: 'cl_calls_last_hour',
      to: '/call-history', threshold: { okIfZero: false },
    },
    {
      icon: <Sparkles />, label: 'Voicemails pending AI transcription', key: 'voicemail_pending_ai',
      to: '/voicemail-ai', threshold: { warn: 5, error: 20 },
    },
  ];

  function classify(n: number | null, t: any) {
    if (n === null || n === undefined) return 'text-textSecondary';
    if (t?.error !== undefined && n >= t.error) return 'text-danger';
    if (t?.warn  !== undefined && n >= t.warn)  return 'text-warning';
    return 'text-success';
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold flex items-center gap-2"><Activity /> System Health</h1>
        <button className="btn" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw size={14} className={`inline mr-1 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {isLoading && <div className="card p-6 text-center text-textSecondary">Loading…</div>}

      {data && (
        <>
          <div className="card p-3 text-xs text-textSecondary">
            Last refresh: {new Date(data.timestamp).toLocaleString()} · auto-refreshes every 30s
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {items.map(it => {
              const v = (data as any)[it.key];
              return (
                <Link key={it.key} to={it.to} className="card p-4 hover:bg-elevated flex items-center gap-3">
                  <div className={classify(v, it.threshold)}>{it.icon}</div>
                  <div className="flex-1">
                    <div className="text-sm text-textSecondary">{it.label}</div>
                    <div className={`text-2xl font-semibold ${classify(v, it.threshold)}`}>
                      {v === null || v === undefined ? '—' : v}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          <div className="card p-4 space-y-2">
            <h2 className="font-semibold">Background workers</h2>
            <p className="text-sm text-textSecondary">
              All 9 workers start at API boot. To verify they're actually running, ssh into
              the host and run:
            </p>
            <pre className="bg-elevated p-2 rounded text-xs overflow-auto">
{`pm2 logs stratus-api --lines 200 | grep -E '(cl_calls_ingestor|wakeup_calls|push_notifier|voicemail_ai_worker|cert_expiry_heartbeat|call_quality_alerts|live_call_sentiment|stir_inbound_hook|events_ws)'`}
            </pre>
            <p className="text-xs text-textSecondary">
              Each worker logs its startup line once, then keeps quiet until it processes
              something. If a worker never logs, check the corresponding .start() in
              <code className="bg-base px-1 mx-1 rounded">api/index.js</code>.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
